import React, { useState } from 'react';
import CraneCard from './CraneCard';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const SearchResults = ({ 
  results = [], 
  isLoading = false, 
  totalCount = 0,
  onGetValuation,
  onLoadMore,
  hasMore = false
}) => {
  const [selectedCranes, setSelectedCranes] = useState(new Set());
  const [sortBy, setSortBy] = useState('relevance');
  const [viewMode, setViewMode] = useState('grid');

  const sortOptions = [
    { value: 'relevance', label: 'Best Match' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'year-new', label: 'Year: Newest First' },
    { value: 'year-old', label: 'Year: Oldest First' },
    { value: 'hours-low', label: 'Hours: Low to High' },
    { value: 'hours-high', label: 'Hours: High to Low' },
    { value: 'dom-new', label: 'Recently Listed' }
  ];

  const handleCompareToggle = (craneId) => {
    const newSelected = new Set(selectedCranes);
    if (newSelected?.has(craneId)) {
      newSelected?.delete(craneId);
    } else if (newSelected?.size < 4) {
      newSelected?.add(craneId);
    }
    setSelectedCranes(newSelected);
  };

  const handleCompareSelected = () => {
    const selectedIds = Array.from(selectedCranes)?.join(',');
    window.location.href = `/comparison-tool?cranes=${selectedIds}`;
  };

  const handleClearSelection = () => {
    setSelectedCranes(new Set());
  };

  if (isLoading && results?.length === 0) {
    return (
      <div className="bg-surface border border-border rounded-lg shadow-card p-8">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <div className="text-center">
            <h3 className="text-lg font-medium text-text-primary mb-2">Searching Cranes...</h3>
            <p className="text-text-secondary">Finding the best matches for your criteria</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isLoading && results?.length === 0) {
    return (
      <div className="bg-surface border border-border rounded-lg shadow-card p-8">
        <div className="text-center">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Search" size={24} className="text-text-secondary" />
          </div>
          <h3 className="text-lg font-medium text-text-primary mb-2">No Cranes Found</h3>
          <p className="text-text-secondary mb-4">
            Try adjusting your search criteria or removing some filters
          </p>
          <Button
            variant="outline"
            onClick={() => window.location?.reload()}
            iconName="RotateCcw"
            iconPosition="left"
          >
            Reset Search
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Results Header */}
      <div className="bg-surface border border-border rounded-lg shadow-card p-4">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div>
              <h2 className="text-lg font-semibold text-text-primary">
                Search Results
              </h2>
              <p className="text-sm text-text-secondary">
                {totalCount?.toLocaleString()} cranes found
              </p>
            </div>
            
            {selectedCranes?.size > 0 && (
              <div className="flex items-center space-x-2 px-3 py-2 bg-primary/10 border border-primary/20 rounded-lg">
                <Icon name="GitCompare" size={16} className="text-primary" />
                <span className="text-sm font-medium text-primary">
                  {selectedCranes?.size} selected
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-3">
            {/* View Mode Toggle */}
            <div className="flex items-center border border-border rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-smooth ${
                  viewMode === 'grid' ?'bg-primary text-primary-foreground' :'text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon name="Grid3X3" size={16} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-smooth ${
                  viewMode === 'list' ?'bg-primary text-primary-foreground' :'text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon name="List" size={16} />
              </button>
            </div>

            {/* Sort Dropdown */}
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
              placeholder="Sort by"
              className="w-48"
            />
          </div>
        </div>

        {/* Comparison Actions */}
        {selectedCranes?.size > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <p className="text-sm text-text-secondary">
                Select up to 4 cranes to compare side by side
              </p>
              <div className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearSelection}
                  iconName="X"
                  iconPosition="left"
                >
                  Clear Selection
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleCompareSelected}
                  disabled={selectedCranes?.size < 2}
                  iconName="GitCompare"
                  iconPosition="left"
                >
                  Compare Selected ({selectedCranes?.size})
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Results Grid/List */}
      <div className={
        viewMode === 'grid' ?'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' :'space-y-4'
      }>
        {results?.map((crane) => (
          <CraneCard
            key={crane?.id}
            crane={crane}
            onGetValuation={onGetValuation}
            onCompare={handleCompareToggle}
            isSelected={selectedCranes?.has(crane?.id)}
          />
        ))}
      </div>
      {/* Load More */}
      {hasMore && (
        <div className="text-center">
          <Button
            variant="outline"
            onClick={onLoadMore}
            loading={isLoading}
            iconName="ChevronDown"
            iconPosition="right"
            className="min-w-32"
          >
            Load More Results
          </Button>
        </div>
      )}
      {/* Loading More Indicator */}
      {isLoading && results?.length > 0 && (
        <div className="text-center py-4">
          <div className="inline-flex items-center space-x-2 text-text-secondary">
            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm">Loading more results...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResults;